import { atom } from 'jotai'

import { enqueueMessageVoice, failVoiceStream, pushVoiceChunk } from '../lib/voice'
import { WebSocketHandler } from '../lib/websocket'
import { applyAgentChunk, type AgentChunk, type AgentChatLine } from './agentChunks'
import { updateSessionAtom } from './session'
import { getClient } from './websocket'

export type ToolUseData = {
  name: string
  input?: Record<string, unknown>
  meta?: Record<string, unknown>
}

export { appendAssistantMessage, sanitizeChatMessages } from './agentMessages'
export type { AgentChunk } from './agentChunks'

/** Expand a pre-chunk frame into one or more typed chunks (order matters). */
function legacyChunksFromData(data: Record<string, unknown>): AgentChunk[] {
  const chunks: AgentChunk[] = []
  if (data['typing']) chunks.push({ type: 'typing' })
  if (data['toolUseStart']) chunks.push({ type: 'toolStart', data: data['toolUseStart'] })
  if (data['toolUse']) chunks.push({ type: 'tool', data: data['toolUse'] })
  if (data['history']) chunks.push({ type: 'history', data: data['history'] })
  if (data['profileUpdate']) chunks.push({ type: 'profileUpdate', data: data['profileUpdate'] })
  if (typeof data['message'] === 'string') {
    chunks.push({
      type: 'text',
      messageId: typeof data['messageId'] === 'string' ? data['messageId'] : undefined,
      data: data['message'],
      done: true,
    })
  }
  if (data['error']) chunks.push({ type: 'error', data: data['error'] })
  if (data['isComplete'] || data['isRejected']) {
    chunks.push({
      type: 'complete',
      data: {
        isComplete: data['isComplete'] === true || data['isRejected'] === true,
        isRejected: data['isRejected'] === true,
      },
    })
  }
  return chunks
}

function chunksFromPayload(data: AgentChunk & Record<string, unknown>): AgentChunk[] {
  if (typeof data.type === 'string') return [data]
  return legacyChunksFromData(data)
}

export type OnboardingMessage = AgentChatLine

export type OnboardingStatus =
  | { type: 'idle' }
  | { type: 'typing' }
  | { type: 'toolUse'; toolUse: ToolUseData }

function isMutedLocally(): boolean {
  try {
    return localStorage.getItem('rheya.voice.muted') === '1'
  } catch {
    return false
  }
}

function applyChunkToMessages(
  get: (atom: any) => unknown,
  set: (atom: any, val: any) => void,
  messagesAtom: any,
  statusAtom: any,
  errorAtom: any,
  loadingAtom: any,
  completeAtom: any,
  rejectedAtom: any | null,
  chunk: AgentChunk,
) {
  const prev = (get(messagesAtom) as AgentChatLine[]) ?? []
  const patch = applyAgentChunk(prev, chunk)

  if (patch.messages) set(messagesAtom, patch.messages)
  if (patch.status === 'typing') set(statusAtom, { type: 'typing' })
  else if (patch.status === 'idle') set(statusAtom, { type: 'idle' })
  else if (patch.status && typeof patch.status === 'object' && 'toolUse' in patch.status) {
    set(statusAtom, { type: 'toolUse', toolUse: patch.status.toolUse })
  }
  if (patch.error != null) {
    set(errorAtom, patch.error)
    set(loadingAtom, false)
    set(statusAtom, { type: 'idle' })
    if (chunk.messageId) failVoiceStream(chunk.messageId, patch.error)
  }
  if (patch.isComplete) {
    set(completeAtom, true)
    set(loadingAtom, false)
    set(statusAtom, { type: 'idle' })
  }
  if (patch.isRejected && rejectedAtom) set(rejectedAtom, true)
  if (patch.profileUpdateFields?.length) {
    console.log('[AgentChat] Profile update received, refreshing session')
    set(updateSessionAtom, undefined as never)
  }

  // Text always visible. Voice is parallel presentation.
  if (patch.voice) {
    pushVoiceChunk(patch.voice.messageId, patch.voice.b64, patch.voice.done)
    if (!isMutedLocally() && (patch.voice.b64 || patch.voice.done)) {
      enqueueMessageVoice(patch.voice.messageId)
    }
  }
  if (chunk.type === 'text' || chunk.type === 'history' || chunk.type === 'tool') {
    set(loadingAtom, false)
  }
}

export const onboardingMessagesAtom = atom<OnboardingMessage[]>([])
export const onboardingIsLoadingAtom = atom(false)
export const onboardingIsCompleteAtom = atom(false)
export const onboardingIsRejectedAtom = atom(false)
export const onboardingErrorAtom = atom<string | null>(null)
export const onboardingStatusAtom = atom<OnboardingStatus>({ type: 'idle' })
/** Shared hold set for live TTS (onboarding + agent chat). */
export const rheyaVoiceHeldMessageIdsAtom = atom<Set<string>>(new Set<string>())

let onboardingHandler: WebSocketHandler | null = null

export const onboardingConnectAtom = atom(null, function connectOnboarding(get, set) {
  const client = getClient()
  if (!client) {
    set(onboardingErrorAtom, 'Not connected')
    return
  }

  if (onboardingHandler) {
    onboardingHandler.close()
  }

  onboardingHandler = client.with('/agents/onboarding')

  onboardingHandler.bind((data: AgentChunk & Record<string, unknown>) => {
    for (const chunk of chunksFromPayload(data)) {
      applyChunkToMessages(
        get,
        set,
        onboardingMessagesAtom,
        onboardingStatusAtom,
        onboardingErrorAtom,
        onboardingIsLoadingAtom,
        onboardingIsCompleteAtom,
        onboardingIsRejectedAtom,
        chunk,
      )
    }
  })

  set(onboardingIsLoadingAtom, true)
  onboardingHandler.send({ voice: !isMutedLocally() })
})

export const onboardingPlayMessageAtom = atom(
  null,
  function playOnboardingMessage(_get, _set, messageId: string) {
    if (!onboardingHandler || !messageId) return
    onboardingHandler.send({ playMessageId: messageId })
  },
)

export const onboardingSetVoiceAtom = atom(
  null,
  function setOnboardingVoice(_get, _set, enabled: boolean) {
    if (!onboardingHandler) return
    onboardingHandler.send({ voice: enabled })
  },
)

export const onboardingVoiceStopAtom = atom(null, function stopOnboardingVoice(_get, _set) {
  if (!onboardingHandler) return
  onboardingHandler.send({ voiceStop: true })
})

export const onboardingSendMessageAtom = atom(
  null,
  function sendOnboardingMessage(_get, set, message: string) {
    if (!onboardingHandler) {
      set(onboardingErrorAtom, 'Not connected to matchmaker')
      return
    }

    set(onboardingMessagesAtom, (prev) => [...prev, { role: 'user', content: message }])
    set(onboardingIsLoadingAtom, true)
    onboardingHandler.send({ message })
  },
)

export const onboardingDisconnectAtom = atom(null, function disconnectOnboarding(_get, _set) {
  if (onboardingHandler) {
    onboardingHandler.close()
    onboardingHandler = null
  }
})

export const onboardingCloseAtom = atom(null, function closeOnboarding(_get, set) {
  // Send close message to backend to mark conversation as complete and process boarding/rejection
  if (onboardingHandler) {
    onboardingHandler.send({ close: true })
    onboardingHandler.close()
    onboardingHandler = null
  }
  set(onboardingMessagesAtom, [])
  set(onboardingIsLoadingAtom, false)
  set(onboardingIsCompleteAtom, false)
  set(onboardingIsRejectedAtom, false)
  set(onboardingErrorAtom, null)
  set(onboardingStatusAtom, { type: 'idle' })
})

export const onboardingResetAtom = atom(null, function resetOnboarding(_get, set) {
  set(onboardingMessagesAtom, [])
  set(onboardingIsLoadingAtom, false)
  set(onboardingIsCompleteAtom, false)
  set(onboardingIsRejectedAtom, false)
  set(onboardingErrorAtom, null)
  set(onboardingStatusAtom, { type: 'idle' })
})

// ============================================================================
// Agent Chat
// ============================================================================

export type AgentChatMessage = AgentChatLine

export type AgentChatStatus =
  | { type: 'idle' }
  | { type: 'typing' }
  | { type: 'toolUse'; toolUse: ToolUseData }

/** Ongoing Rheya chat (restored), one-shot profile edit, or unmatch debrief. */
export type AgentChatMode = 'general' | 'profile' | 'unmatch'

export type AgentChatOpenOptions = {
  mode?: AgentChatMode
  /** Invisible bootstrap context (not shown as a user bubble). */
  initialContext?: string
}

export const agentChatMessagesAtom = atom<AgentChatMessage[]>([])
export const agentChatIsLoadingAtom = atom(false)
export const agentChatIsCompleteAtom = atom(false)
export const agentChatErrorAtom = atom<string | null>(null)
export const agentChatIsOpenAtom = atom(false)
export const agentChatIsMinimizedAtom = atom(false)
export const agentChatInitialContextAtom = atom<string | undefined>(undefined)
export const agentChatModeAtom = atom<AgentChatMode>('general')
export const agentChatStatusAtom = atom<AgentChatStatus>({ type: 'idle' })
/** @deprecated use rheyaVoiceHeldMessageIdsAtom */
export const agentChatHeldMessageIdsAtom = rheyaVoiceHeldMessageIdsAtom

let agentChatHandler: WebSocketHandler | null = null

export type AgentChatConnectOptions = {
  mode?: AgentChatMode
  initialContext?: string | undefined
}

export function agentPathForMode(mode: AgentChatMode): string {
  if (mode === 'profile') return '/agents/profile'
  if (mode === 'unmatch') return '/agents/unmatch'
  return '/agents/general'
}

export const agentChatConnectAtom = atom(
  null,
  function connectAgentChat(get, set, options: AgentChatConnectOptions = {}) {
    const mode = options.mode ?? get(agentChatModeAtom)
    const initialContext = options.initialContext ?? get(agentChatInitialContextAtom)

    const client = getClient()
    if (!client) {
      set(agentChatErrorAtom, 'Not connected')
      return
    }

    if (agentChatHandler) {
      agentChatHandler.close()
    }

    set(agentChatModeAtom, mode)
    set(agentChatMessagesAtom, [])
    set(agentChatIsCompleteAtom, false)
    set(agentChatErrorAtom, null)

    agentChatHandler = client.with(agentPathForMode(mode))

    agentChatHandler.bind((data: AgentChunk & Record<string, unknown>) => {
      console.log('[AgentChat] Received data:', data)
      for (const chunk of chunksFromPayload(data)) {
        applyChunkToMessages(
          get,
          set,
          agentChatMessagesAtom,
          agentChatStatusAtom,
          agentChatErrorAtom,
          agentChatIsLoadingAtom,
          agentChatIsCompleteAtom,
          null,
          chunk,
        )
      }
    })

    set(agentChatIsLoadingAtom, true)
    // One bootstrap frame only — each send can spawn a Velaros handler chain.
    // Multiple opens on the same multiplex id double every text/voice delta.
    const bootstrap: { voice: boolean; context?: string } = {
      voice: !isMutedLocally(),
    }
    if (initialContext) bootstrap.context = initialContext
    agentChatHandler.send(bootstrap)
  },
)

/** Request ad-hoc TTS for a stored message (restored thread / manual play). */
export const agentChatPlayMessageAtom = atom(
  null,
  function playAgentMessage(_get, _set, messageId: string) {
    if (!agentChatHandler || !messageId) return
    agentChatHandler.send({ playMessageId: messageId })
  },
)

export const agentChatSetVoiceAtom = atom(
  null,
  function setAgentVoice(_get, _set, enabled: boolean) {
    if (!agentChatHandler) return
    agentChatHandler.send({ voice: enabled })
  },
)

export const agentChatVoiceStopAtom = atom(null, function stopAgentVoice(_get, _set) {
  if (!agentChatHandler) return
  agentChatHandler.send({ voiceStop: true })
})

export const agentChatSendMessageAtom = atom(
  null,
  function sendAgentChatMessage(_get, set, message: string) {
    if (!agentChatHandler) {
      set(agentChatErrorAtom, 'Not connected')
      return
    }

    set(agentChatMessagesAtom, (prev) => [...prev, { role: 'user', content: message }])
    set(agentChatIsLoadingAtom, true)
    agentChatHandler.send({ message })
  },
)

export const agentChatSendContextAtom = atom(
  null,
  function sendAgentChatContext(_get, set, context: string) {
    if (!agentChatHandler) {
      set(agentChatErrorAtom, 'Not connected')
      return
    }

    set(agentChatIsLoadingAtom, true)
    agentChatHandler.send({ context })
  },
)

export const agentChatDisconnectAtom = atom(null, function disconnectAgentChat(get, _set) {
  // Profile sessions are ephemeral: complete on disconnect so edit threads
  // never resume into general history.
  if (agentChatHandler && get(agentChatModeAtom) === 'profile') {
    try {
      agentChatHandler.send({ close: true })
    } catch {
      // ignore
    }
  }
  if (agentChatHandler) {
    agentChatHandler.close()
    agentChatHandler = null
  }
})

export const agentChatResetAtom = atom(null, function resetAgentChat(_get, set) {
  set(agentChatMessagesAtom, [])
  set(agentChatIsLoadingAtom, false)
  set(agentChatIsCompleteAtom, false)
  set(agentChatErrorAtom, null)
  set(agentChatInitialContextAtom, undefined)
  set(agentChatModeAtom, 'general')
  set(agentChatStatusAtom, { type: 'idle' })
})

export const agentChatOpenAtom = atom(
  null,
  function openAgentChat(_get, set, options?: string | AgentChatOpenOptions) {
    // Back-compat: openChat('context string') → general mode with context
    if (typeof options === 'string') {
      set(agentChatModeAtom, 'general')
      set(agentChatInitialContextAtom, options)
    } else {
      set(agentChatModeAtom, options?.mode ?? 'general')
      set(agentChatInitialContextAtom, options?.initialContext)
    }
    // Profile edit is a focused session — never open minimized
    const mode = typeof options === 'string' ? 'general' : (options?.mode ?? 'general')
    if (mode === 'profile') set(agentChatIsMinimizedAtom, false)
    set(agentChatIsOpenAtom, true)
  },
)

export const agentChatMinimizeAtom = atom(null, function minimizeAgentChat(_get, set) {
  set(agentChatIsMinimizedAtom, true)
})

export const agentChatRestoreAtom = atom(null, function restoreAgentChat(_get, set) {
  set(agentChatIsMinimizedAtom, false)
})

export const agentChatCloseAtom = atom(null, function closeAgentChat(get, set) {
  const mode = get(agentChatModeAtom)
  if (agentChatHandler) {
    // Profile edit: complete the session so it never joins general history.
    // General / unmatch: leave incomplete so the next open / badge restores history.
    // (Unmatch fail-closed runs on explicit exit tool or scanner, not FAB dismiss.)
    if (mode === 'profile') {
      try {
        agentChatHandler.send({ close: true })
      } catch {
        // ignore
      }
    }
    agentChatHandler.close()
    agentChatHandler = null
  }
  set(agentChatIsOpenAtom, false)
  set(agentChatIsMinimizedAtom, false)
  set(agentChatMessagesAtom, [])
  set(agentChatIsLoadingAtom, false)
  set(agentChatIsCompleteAtom, false)
  set(agentChatErrorAtom, null)
  set(agentChatInitialContextAtom, undefined)
  set(agentChatModeAtom, 'general')
  set(agentChatStatusAtom, { type: 'idle' })
})
